import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

const url_t = process.env.REACT_APP_GLOBAL_URL;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  padding: 40px;
  position: relative;
  max-height: 100vh;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  z-index: 500;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #2c3e50;
  line-height: 1.5;
  margin-bottom: 15px;
`;

const HighlightedText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #e74c3c;
`;

const SuccessOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const SuccessContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const SuccessImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
`;

const ViewOrderButton = styled.button`
  background: linear-gradient(45deg, #e74c3c, #c0392b);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.2s;
  pointer-events: auto;

  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(45deg, #c0392b, #e74c3c);
  }
`;

const CheckoutCart = ({ state, onClose, onOrderPlaced }) => {
  const [userId, setUserId] = useState(null);
  const cartItems = state?.cartItems || [];
  const totalPrice = state?.totalPrice || 0;
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({ name: false, contact_number: false });
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    contact_number: "",
    name: "",
    estimated_date: "",
  });

  const getFallbackUserId = () => {
    try {
      const rawUserId = localStorage.getItem("userId");
      if (rawUserId && /^\d+$/.test(rawUserId)) {
        return parseInt(rawUserId, 10);
      }

      const rawLoginData = localStorage.getItem("loginData");
      if (!rawLoginData) return null;

      const parsedLoginData = JSON.parse(rawLoginData);
      if (!parsedLoginData?.id) return null;

      return parseInt(parsedLoginData.id, 10);
    } catch (parseError) {
      console.error("Error parsing fallback user id:", parseError);
      return null;
    }
  };

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const sessionId = localStorage.getItem("sessionId");
        const fallbackUserId = getFallbackUserId();

        if (fallbackUserId) {
          setUserId(fallbackUserId);
          localStorage.setItem("userId", String(fallbackUserId));
        }

        if (!sessionId && !fallbackUserId) {
          console.error("No session ID found");
          return;
        }

        const profileLookupId = /^\d+$/.test(sessionId || "") ? sessionId : fallbackUserId;
        if (!profileLookupId) {
          return;
        }

        const response = await fetch(url_t + "auth/user_profile", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "session-id": String(profileLookupId),
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.user?.id) {
            setUserId(data.user.id);
            localStorage.setItem("userId", String(data.user.id));
          }
        } else {
          if (!fallbackUserId) {
            console.error("Failed to fetch user profile");
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserId();
  }, []);

  useEffect(() => {
    const calculateEstimatedDate = () => {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const formattedDate = tomorrow.toISOString().split("T")[0];
      setFormData((prev) => ({ ...prev, estimated_date: formattedDate }));
    };

    calculateEstimatedDate();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handlePlaceOrder = async () => {
    const effectiveUserId = userId || getFallbackUserId();

    if (!effectiveUserId) {
      alert("User not found. Please log in first.");
      return;
    }

    if (!formData.name || !formData.contact_number || !formData.estimated_date) {
      setErrors({
        name: !formData.name,
        contact_number: !formData.contact_number,
        estimated_date: !formData.estimated_date,
      });
      return;
    }

    const sessionId = localStorage.getItem("sessionId");

    const requestHeaders = {
      "Content-Type": "application/json",
    };

    if (sessionId) {
      requestHeaders["session-id"] = sessionId;
    }

    try {
      for (const item of cartItems) {
        const orderData = {
          user_id: effectiveUserId,
          product_id: item.id,
          total_amount: (item.price * item.quantity).toFixed(2),
          status: "Pending",
          quantity: item.quantity,
          contact_number: formData.contact_number,
          name: formData.name,
          estimated_date: formData.estimated_date,
        };

        const response = await fetch(url_t + "order/order_insert", {
          method: "POST",
          headers: requestHeaders,
          body: JSON.stringify(orderData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to place order");
        }

        // Explicitly consume the success response to prevent browser alert
        const data = await response.json();
        console.log(data); // Log the response for debugging (optional)
      }

      setShowSuccess(true);
      onOrderPlaced();
    } catch (error) {
      console.error("Error placing order:", error);
      alert(`Failed to place order: ${error.message}`);
    }
  };

  const handleViewOrder = () => {
    setShowSuccess(false);
    onClose();
    navigate("/OrderList");
  };

  return (
    <>
      <ModalContainer>
        <CloseButton onClick={onClose}>×</CloseButton>
        <CheckoutTitle>Checkout Cart</CheckoutTitle>
        <CheckoutInfo>
          <ProductSection>
            {cartItems.length > 0 ? (
              cartItems.map((item, index) => (
                <ProductDetails key={index}>
                  <ProductImage src={item.imageUrl} alt={item.product_name} />
                  <ProductInfo>
                    <ProductName>{item.product_name}</ProductName>
                    <ProductDescription>
                      {item.description || "No description available"}
                    </ProductDescription>
                    <PriceQuantityContainer>
                      <ProductPrice>₱{(item.price * item.quantity).toFixed(2)}</ProductPrice>
                      <ProductQuantity>Quantity: {item.quantity}</ProductQuantity>
                    </PriceQuantityContainer>
                  </ProductInfo>
                </ProductDetails>
              ))
            ) : (
              <p>No items in cart.</p>
            )}

            <FormSection>
              <InfoText>
                Your order will be available for pickup on <strong>BISU-BILAR Campus</strong> at{' '}
                <strong>COMSOC office</strong> the day after purchase.
              </InfoText>
              <FormField>
                <Label>Name</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  required
                  style={{ borderColor: errors.name ? "red" : "#ddd" }}
                />
              </FormField>
              <FormField>
                <Label>Contact Number</Label>
                <Input
                  type="tel"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  placeholder="Enter contact number"
                  required
                  style={{ borderColor: errors.contact_number ? "red" : "#ddd" }}
                />
              </FormField>
              <FormField>
                <Label>Estimated Date:</Label>
                <HighlightedText>
                  {formData.estimated_date
                    ? new Date(formData.estimated_date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : ""}
                </HighlightedText>
              </FormField>
              <FormField>
                <Label>Payment Method:</Label>
                <InfoText>Payment will be made in person upon pickup.</InfoText>
              </FormField>
            </FormSection>
          </ProductSection>

          <TotalPriceSection>
            <TotalPriceContainer>
              <TotalPriceTitle>Total Amount</TotalPriceTitle>
              <TotalPrice>₱{totalPrice.toFixed(2)}</TotalPrice>
            </TotalPriceContainer>
            <PlaceOrderButton onClick={handlePlaceOrder}>Place Order</PlaceOrderButton>
          </TotalPriceSection>
        </CheckoutInfo>
      </ModalContainer>

      {showSuccess && (
        <SuccessOverlay>
          <SuccessContainer>
            <SuccessImage
              src="https://via.placeholder.com/100?text=Success"
              alt="Order Success"
            />
            <InfoText>Order placed successfully!</InfoText>
            <ViewOrderButton onClick={handleViewOrder}>View Order</ViewOrderButton>
          </SuccessContainer>
        </SuccessOverlay>
      )}
    </>
  );
};

// Existing styled components remain unchanged
const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  cursor: pointer;
  font-size: 16px;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
    background: #cc0000;
  }
`;

const CheckoutTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #2c3e50;
  text-align: center;
`;

const CheckoutInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

const ProductSection = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProductDetails = styled.div`
  display: flex;
  gap: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
`;

const ProductImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const PriceQuantityContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;

const ProductName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
`;

const ProductDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  line-height: 1.5;
`;

const ProductPrice = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #e74c3c;
`;

const ProductQuantity = styled.p`
  font-size: 14px;
  color: #666;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    border-color: #e74c3c;
    outline: none;
  }
`;

const TotalPriceSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;
`;

const TotalPriceContainer = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  width: 100%;
  text-align: right;
`;

const TotalPriceTitle = styled.h4`
  font-size: 16px;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const TotalPrice = styled.p`
  font-size: 24px;
  font-weight: 700;
  color: #e74c3c;
`;

const PlaceOrderButton = styled.button`
  background: linear-gradient(45deg, #e74c3c, #c0392b);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(45deg, #c0392b, #e74c3c);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default CheckoutCart;